import { useEffect, useRef, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { de } from '../../lib/i18n/de';
import { STEPS, type StepKey, type StepDescriptor } from '../hub/dimensions';
import { useWizardAudience } from './WizardAudienceContext';

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Wer schaut sich diesen Step an?
 *  - 'berater' (default): Cockpit-Links und Berater-spezifische Footer-Hinweise
 *  - 'mandant': vereinfachter Footer (Sie-Form, kein „Validierung fehlt", kein
 *    Cockpit-Verweis), Navigation geht zurück auf /mandant/case/:id statt /case/:id/hub
 */
export type WizardAudience = 'berater' | 'mandant';

interface WizardStepProps {
  /** Welcher Step gerade aktiv ist (für Header + Navigation) */
  stepId: StepKey;
  /** Der Body (Eingabefelder, Erläuterungstexte) */
  children: ReactNode;
  /** Validierungs-Resultat: ist der „Weiter"-Button klickbar? */
  canProceed: boolean;
  /** Optional: Letzter Auto-Save-Zeitpunkt zur Anzeige */
  lastSavedAt?: string | null | undefined;
  /** Optional: Save-Status für Live-Indikator */
  saveState?: SaveState | undefined;
  /** Wenn gesetzt, wird beim „Weiter"-Klick ausgeführt vor der Navigation */
  onProceed?: (() => void) | undefined;
  /**
   * Wer ist das Publikum dieser Step-Ansicht? Default 'berater'.
   * Beeinflusst Navigation-Pfade und Footer-Tonalität.
   */
  audience?: WizardAudience | undefined;
}

/**
 * Generischer Wizard-Step-Wrapper im Editorial-Stil.
 *
 * Header: große Modul-Nummer + serif-uppercase Titel + italic Subtitle.
 * Body: durchgereicht.
 * Footer: Auto-Save-Indikator links, „Zurück / Weiter"-Buttons rechts.
 *
 * Die Reihenfolge der Steps wird aus STEPS abgeleitet — der „Weiter"-Button
 * springt automatisch zum nächsten Step. Beim letzten Step wird daraus
 * „Modul abschließen" und es geht zurück in den Hub.
 */
export function WizardStep({
  stepId,
  children,
  canProceed,
  lastSavedAt,
  saveState = 'idle',
  onProceed,
  audience: audienceProp,
}: WizardStepProps) {
  // Audience: explizite Prop hat Vorrang vor Context (dann Default 'berater').
  // Damit funktionieren die existierenden Step-Komponenten (TriageStep etc.)
  // ohne Anpassung im Mandanten-Flow — der Mandant-Routes-Wrapper setzt
  // den Audience-Provider und die Steps merken nichts davon.
  const audienceFromContext = useWizardAudience();
  const audience = audienceProp ?? audienceFromContext;

  // Routing-Params:
  //   - Berater-Pfad nutzt /case/:id/...      → useParams<{ id }>()
  //   - Mandant-Pfad nutzt /mandant/case/:id/... — wir nutzen denselben URL-Param
  //     `:id`, damit die existierenden Step-Komponenten weiter funktionieren.
  const params = useParams<{ id?: string }>();
  const caseId = params.id ?? 'demo';
  const navigate = useNavigate();

  // Fokus-Management (WCAG 2.4.3): Nach „Weiter"/„Zurück" wird die alte
  // Step-Komponente unmountet und der Fokus fiele auf <body>. Wir setzen ihn
  // programmatisch auf den Step-Titel, damit Tastatur- und Screenreader-Nutzer
  // den neuen Schritt-Kontext bekommen. Pro Step-Wechsel neu, weil jeder Step
  // seine eigene WizardStep-Instanz rendert.
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    titleRef.current?.focus();
  }, [stepId]);

  const currentIndex = STEPS.findIndex((s) => s.id === stepId);
  const step: StepDescriptor | undefined = currentIndex >= 0 ? STEPS[currentIndex] : undefined;
  const previousStep = currentIndex > 0 ? STEPS[currentIndex - 1] : null;
  const nextStep =
    currentIndex >= 0 && currentIndex < STEPS.length - 1 ? STEPS[currentIndex + 1] : null;
  const isLast = !nextStep;

  if (!step) {
    return (
      <p className="font-serif italic text-stone-500">
        Unbekannter Schritt: <code>{stepId}</code>
      </p>
    );
  }

  const wizardBase =
    audience === 'mandant' ? `/mandant/case/${caseId}/wizard` : `/case/${caseId}/wizard`;
  const homeBase = audience === 'mandant' ? `/mandant/case/${caseId}` : `/case/${caseId}/hub`;

  const handlePrev = () => {
    if (previousStep) {
      void navigate(`${wizardBase}/${previousStep.id}`);
    } else {
      void navigate(homeBase);
    }
  };

  const handleNext = () => {
    onProceed?.();
    if (isLast) {
      void navigate(homeBase);
    } else if (nextStep) {
      void navigate(`${wizardBase}/${nextStep.id}`);
    }
  };

  // Editorial-Header: Titel hat \n für den Zeilenumbruch.
  const titleSource = readTitle(stepId);
  const titleLines = titleSource.split('\n');

  return (
    <section
      aria-labelledby="wizard-step-title"
      className="rounded-sm border border-stone-200 bg-white"
    >
      <header className="relative flex flex-col gap-3 border-b border-stone-200 px-6 pb-8 pt-10 md:px-10 md:pb-10 md:pt-14">
        <div
          aria-hidden="true"
          className="absolute right-6 top-6 h-12 w-12 rounded-sm md:right-10 md:top-10 md:h-16 md:w-16"
          style={{ backgroundColor: step.accentHex, opacity: 0.4 }}
        />
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          Modul {step.nummer}
        </p>
        <h1
          id="wizard-step-title"
          ref={titleRef}
          tabIndex={-1}
          className="font-serif text-3xl font-black leading-none tracking-tight text-stone-900 outline-none md:text-5xl"
        >
          {titleLines.map((line, i) => (
            <span key={i} className="block">
              {line}
            </span>
          ))}
        </h1>
        <p className="font-serif text-base italic text-stone-500 md:text-lg">{step.subtitle}</p>
      </header>

      <div className="px-6 py-8 md:px-10 md:py-10">{children}</div>

      <footer className="flex flex-col items-stretch gap-4 border-t border-stone-200 px-6 py-6 md:flex-row md:items-center md:justify-between md:px-10">
        {/* Mandanten-Flow zeigt den Save-Status bereits sticky im
            MandantWizardWrapper — hier würde ein zweiter Indikator doppeln.
            Leerer Platzhalter erhält die Footer-Ausrichtung (justify-between). */}
        {audience === 'mandant' ? (
          <div aria-hidden="true" />
        ) : (
          <SaveIndicator state={saveState} lastSavedAt={lastSavedAt ?? null} />
        )}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handlePrev}
            className="inline-flex items-center justify-center rounded-sm border border-stone-200 bg-white px-5 py-3 font-sans text-xs font-medium uppercase tracking-widest text-stone-700 transition-colors hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
          >
            &larr; {de.wizard.previous}
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={!canProceed}
            className="inline-flex items-center justify-center rounded-sm bg-stone-900 px-6 py-3 font-sans text-xs font-medium uppercase tracking-widest text-white transition-colors hover:bg-stone-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-stone-300"
          >
            {isLast ? de.wizard.finish : `${de.wizard.next} →`}
          </button>
        </div>
      </footer>
    </section>
  );
}

function SaveIndicator({ state, lastSavedAt }: { state: SaveState; lastSavedAt: string | null }) {
  if (state === 'saving') {
    return (
      <span className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        {de.wizard.saving}
      </span>
    );
  }
  if (state === 'error') {
    return (
      <span className="font-sans text-xs font-medium uppercase tracking-widest text-rose-600">
        {de.wizard.saveError}
      </span>
    );
  }
  if (state === 'saved' || lastSavedAt) {
    const ts = lastSavedAt ? formatTime(lastSavedAt) : null;
    return (
      <span className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        {de.wizard.saved}
        {ts ? ` · ${ts}` : ''}
      </span>
    );
  }
  return (
    <span className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
      {de.wizard.autosaveExplain}
    </span>
  );
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

/** Mapping StepKey → Editorial-Title aus i18n. Title kann `\n` enthalten. */
function readTitle(stepId: StepKey): string {
  switch (stepId) {
    case 'triage':
      return de.steps.triage.title;
    case 'identity':
      return de.steps.identity.title;
    case 'activity':
      return de.steps.activity.title;
    case 'style':
      return de.steps.style.title;
    case 'resources':
      return de.steps.resources.title;
    case 'health':
      return de.steps.health.title;
    case 'private':
      return de.steps.private.title;
  }
}
